"use client"
import React, { useRef, useState, useCallback } from 'react';
import { Button } from './button';
import { Badge } from './badge';
import { FileUp, LucideIcon, Loader2 } from 'lucide-react';

export interface FileUploadAreaProps {
  /**
   * The accepted MIME type for file uploads
   */
  acceptedMimeType?: string;
  
  /**
   * The file format name to display (e.g., "CSV", "JSON")
   * Can be a single string or an array of format names
   */
  formatName: string | string[];
  
  /**
   * The file extension to display (e.g., ".csv", ".json")
   */
  fileExtension?: string;
  
  /**
   * Whether to allow multiple file uploads
   */
  multiple?: boolean;
  
  /**
   * Callback function when files are uploaded
   */
  onFilesSelected: (files: FileList | null) => void;
  
  /**
   * Custom class name for the upload area
   */
  className?: string;
  
  /**
   * Custom button text (defaults to "Open {formatName} Files")
   */
  buttonText?: string;

  /**
   * Custom icon to display in the upload area
   * Defaults to FileUp icon if not provided
   */
  icon?: React.ReactNode;

  /**
   * Custom icon for the upload button
   * Defaults to FileUp icon if not provided
   */
  buttonIcon?: React.ReactNode;
  
  /**
   * Whether to hide the format badge
   */
  hideBadge?: boolean;

  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean;
}

export function FileUploadArea({
  acceptedMimeType,
  formatName,
  fileExtension,
  multiple = false,
  onFilesSelected,
  className = '',
  buttonText,
  icon,
  buttonIcon,
  hideBadge = false,
  isLoading = false
}: FileUploadAreaProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Handle both string and array formats
  const formatDisplay = Array.isArray(formatName) 
    ? formatName.length > 0 ? 'file' : '' 
    : formatName;
  
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (!isLoading && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFilesSelected(e.dataTransfer.files);
    }
  }, [onFilesSelected, isLoading]);

  const handleFileUpload = useCallback((uploadedFiles: FileList | null) => {
    if (isLoading || !uploadedFiles || uploadedFiles.length === 0) return;
    
    onFilesSelected(uploadedFiles);
    
    // Reset the file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFilesSelected, isLoading]);

  // Function to trigger file upload dialog
  const triggerFileUpload = useCallback(() => {
    if (!isLoading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isLoading]);

  // Render format badges if formatName is an array
  const renderFormatBadges = () => {
    if (hideBadge) return null;
    
    if (Array.isArray(formatName) && formatName.length > 0) {
      return (
        <div className="flex flex-wrap justify-center gap-2">
          <Badge variant="outline" className="bg-background/80 backdrop-blur-sm dark:bg-background/30 dark:border-border/70 text-muted-foreground">
            Accepts {formatName.join(', ')} {multiple ? 'files' : 'file'}
          </Badge>
        </div>
      );
    }
    
    // Original single format badge
    return (
      <div className="flex flex-wrap justify-center gap-2">
        <Badge variant="outline" className="bg-background/80 backdrop-blur-sm dark:bg-background/30 dark:border-border/70 text-muted-foreground">
          Accepts {multiple ? `${formatDisplay} files` : `${formatDisplay} file`} {fileExtension && `(${fileExtension})`}
        </Badge>
      </div>
    );
  };

  return (
    <div 
      className={`group relative flex flex-col items-center justify-center p-10 border-2 border-dashed rounded-lg shadow-sm transition-all duration-200 overflow-hidden ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'} ${
        isDragging 
          ? 'border-primary/70 shadow-md dark:border-primary/80 bg-primary/5' 
          : 'border-border dark:border-border/70 dark:bg-card hover:border-primary/60 hover:shadow-md hover:bg-primary/10 dark:hover:border-primary/70 dark:hover:bg-primary/15'
      } ${className}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={triggerFileUpload}
    >
      {/* Background gradient with opacity overlay */}
      <div className={`absolute inset-0 transition-all duration-200 ${
        isDragging 
          ? 'bg-aurora-gradient-alt opacity-20 dark:opacity-30' 
          : 'bg-aurora-gradient opacity-15 dark:bg-aurora-gradient-cool dark:opacity-25 group-hover:opacity-25 dark:group-hover:opacity-40'
      }`}></div>
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-20 flex items-center justify-center">
          <div className="flex items-center gap-2 text-primary">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="font-medium">Processing...</span>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="relative z-10 w-full">
        <input
          type="file"
          accept={acceptedMimeType}
          multiple={multiple}
          onChange={(e) => handleFileUpload(e.target.files)}
          className="hidden"
          id="file-input"
          ref={fileInputRef}
          disabled={isLoading}
        />
        <div className="flex flex-col items-center justify-center w-full py-8">
          <div className="w-16 h-16 mb-5 flex items-center justify-center rounded-full bg-background/80 backdrop-blur-sm dark:bg-background/40 dark:border dark:border-border/30 transition-all duration-200 group-hover:bg-primary/10 group-hover:border-primary/30 dark:group-hover:bg-primary/20 dark:group-hover:border-primary/40">
            {icon || (
              <svg
                className="w-8 h-8 text-primary dark:text-primary/90 transition-all duration-200 group-hover:text-primary/90 dark:group-hover:text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                ></path>
              </svg>
            )}
          </div>
          <span className="text-2xl font-semibold text-foreground mb-3 text-center dark:text-foreground/90 transition-colors duration-200 group-hover:text-foreground dark:group-hover:text-foreground">
            Click anywhere to select {multiple ? 'files' : 'a file'}
          </span>
          <span className="text-sm text-muted-foreground text-center dark:text-muted-foreground/90 transition-colors duration-200 group-hover:text-foreground/80 dark:group-hover:text-foreground/80">
            or drag and drop {multiple ? 'files' : 'a file'} here
          </span>
          <div className="mt-6 flex flex-col items-center gap-4">
            {buttonText !== "" && (
              <Button 
                variant="default"
                size="default"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent double triggering
                  triggerFileUpload();
                }}
                className="dark:shadow-md dark:shadow-primary/10"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {buttonIcon || <FileUp className="w-4 h-4 mr-2" />}
                    {buttonText || `Open ${multiple ? 'files' : 'file'}`}
                  </>
                )}
              </Button>
            )}
            {renderFormatBadges()}
          </div>
        </div>
      </div>
    </div>
  );
} 